// src/services/LiveHelperChatService.js
//
// Loads the Life on Life support chat widget (Live Helper Chat) exactly once.
// Replaces the old Zendesk widget integration.

const LHC_STATUS_CONTAINER_ID = 'lhc_status_container_page'
const LHC_WIDGET_SCRIPT_ID = 'lhc_widgetv2_script'
let suppressionObserver = null
let suppressionInterval = null

function ensureStatusContainerExists() {
  if (typeof document === 'undefined') return
  const existing = document.getElementById(LHC_STATUS_CONTAINER_ID)
  if (existing) {
    // Ensure the container lives under <body> so it never affects app layout.
    if (existing.parentElement !== document.body) {
      document.body.appendChild(existing)
    }
    // Ensure fixed positioning (bottom-right) so widget doesn't push page content.
    existing.style.position = 'fixed'
    existing.style.right = '16px'
    existing.style.bottom = '16px'
    existing.style.zIndex = '2147483000'
    existing.style.width = '0px'
    existing.style.height = '0px'
    existing.style.overflow = 'visible'
    existing.style.display = 'block'
    return
  }

  const div = document.createElement('div')
  div.id = LHC_STATUS_CONTAINER_ID
  div.style.position = 'fixed'
  div.style.right = '16px'
  div.style.bottom = '16px'
  div.style.zIndex = '2147483000'
  div.style.width = '0px'
  div.style.height = '0px'
  div.style.overflow = 'visible'
  div.style.display = 'block'
  document.body.appendChild(div)
}

function normalizeLhcConfig(config = {}) {
  const lhcBasePath =
    typeof config.lhcBasePath === 'string' && config.lhcBasePath.trim()
      ? config.lhcBasePath.trim()
      : '/lhc/lhc_web/index.php/'
  const widgetScriptPath =
    typeof config.widgetScriptPath === 'string' && config.widgetScriptPath.trim()
      ? config.widgetScriptPath.trim()
      : '/lhc/lhc_web/design/defaulttheme/js/widgetv2/index.js'
  const fallbackStartUrl =
    typeof config.fallbackStartUrl === 'string' && config.fallbackStartUrl.trim()
      ? config.fallbackStartUrl.trim()
      : 'https://support.lifeonlife.org/lhc/lhc_web/index.php/en/chat/start'

  return { lhcBasePath, widgetScriptPath, fallbackStartUrl }
}

// Ensures window.LHC_API.args.lhc_base_url is always defined so that the
// widget's internal error logger (which reads that path unconditionally)
// never throws "Cannot read properties of undefined". Safe to call early
// (before initLiveHelperChat) and repeatedly.
export function primeLhcApi(config = {}) {
  if (typeof window === 'undefined') return
  const normalized = normalizeLhcConfig(config)
  const origin = window.location?.origin || ''
  window.LHC_API = window.LHC_API || {}
  window.LHC_API.args = window.LHC_API.args || {}
  if (!window.LHC_API.args.lhc_base_url) {
    window.LHC_API.args.lhc_base_url = `${origin}${normalized.lhcBasePath}`
  }
}

export function initLiveHelperChat(config = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  // Ensure the render target exists (even if App.vue hasn't rendered yet).
  ensureStatusContainerExists()

  // Idempotent: don't inject the widget script twice.
  if (document.getElementById(LHC_WIDGET_SCRIPT_ID)) return

  // Preserve any existing LHC_API usage.
  window.LHC_API = window.LHC_API || {}

  const origin = window.location?.origin || ''
  const normalized = normalizeLhcConfig(config)

  // Always load LHC through our own origin at /lhc/...:
  // - localhost: handled by Vue devServer proxy
  // - deployed: handled by Firebase Hosting rewrite -> Cloud Function proxy
  const lhcBaseUrl = `${origin}${normalized.lhcBasePath}`
  const widgetIndexUrl = `${origin}${normalized.widgetScriptPath}`

  window.LHC_API.args = {
    // "widget" mode shows the launcher icon and opens the chat UI on interaction.
    // "embed" mode can render the leave-a-message UI inline, which we don't want.
    mode: 'widget',
    lhc_base_url: lhcBaseUrl,
    wheight: 450,
    wwidth: 350,
    pheight: 520,
    pwidth: 500,
    domain: 'web.app',
    leaveamessage: true,
    check_messages: false,
  }

  // This matches the client's embed logic for language inference.
  try {
    let _l = ''
    const _m = document.getElementsByTagName('meta')
    let _cl = ''
    for (let i = 0; i < _m.length; i++) {
      if (_m[i].getAttribute('http-equiv') == 'content-language') {
        _cl = _m[i].getAttribute('content')
      }
    }
    if (document.documentElement.lang != '') _l = document.documentElement.lang
    if (_cl != '' && _cl != _l) _l = _cl
    if (_l == undefined || _l == '') {
      _l = ''
    } else {
      _l = _l[0].toLowerCase() + _l[1].toLowerCase()
      if ('eng' == _l) {
        _l = ''
      } else {
        window.LHC_API.args.lang = _l + '/'
      }
    }
  } catch (e) {
    // non-fatal; widget will default language
  }

  const po = document.createElement('script')
  po.id = LHC_WIDGET_SCRIPT_ID
  po.type = 'text/javascript'
  po.async = true

  const date = new Date()
  po.src =
    widgetIndexUrl +
    '?' +
    date.getFullYear() +
    date.getMonth() +
    date.getDate()

  const s = document.getElementsByTagName('script')[0]
  s.parentNode.insertBefore(po, s)
}

export function hideLiveHelperChat() {
  if (typeof document === 'undefined') return
  const scriptEl = document.getElementById(LHC_WIDGET_SCRIPT_ID)
  if (scriptEl && scriptEl.parentNode) {
    scriptEl.parentNode.removeChild(scriptEl)
  }

  const container = document.getElementById(LHC_STATUS_CONTAINER_ID)
  if (container && container.parentNode) {
    container.parentNode.removeChild(container)
  }

  // Best-effort cleanup for any LHC-created floating iframes/popups.
  const lhcIframes = document.querySelectorAll(
    'iframe[src*="/lhc/"], iframe[src*="support.lifeonlife.org/lhc"]'
  )
  lhcIframes.forEach((el) => {
    if (el.parentNode) el.parentNode.removeChild(el)
  })

  // LHC can inject non-iframe floating nodes directly in body (e.g. status-widgetv2_*).
  const floatingNodes = document.querySelectorAll(
    [
      '[class*="status-widgetv2"]',
      '[class*="status_widget_v2"]',
      '[class*="status-widget"]',
      '[class*="status_widget"]',
      '[id*="status-widget"]',
      '[id*="status_widget"]',
      '.start-chat',
      '.start-chat-mobile',
      '.start-chat-desktop',
    ].join(', ')
  )
  floatingNodes.forEach((el) => {
    if (el.parentNode) el.parentNode.removeChild(el)
  })

  // Reset API object so stale open handlers don't revive old UI, but
  // preserve args.lhc_base_url so the widget's internal error logger
  // (which reads it unconditionally) never throws on late-loading scripts.
  if (typeof window !== 'undefined') {
    const preservedBaseUrl = window.LHC_API?.args?.lhc_base_url
    window.LHC_API = preservedBaseUrl
      ? { args: { lhc_base_url: preservedBaseUrl } }
      : {}
  }
}

export function showLiveHelperChat() {
  if (typeof document === 'undefined') return
  const container = document.getElementById(LHC_STATUS_CONTAINER_ID)
  if (!container) return
  container.style.display = 'block'
}

export function startLiveHelperChatSuppression() {
  if (typeof document === 'undefined') return

  // Immediately remove anything already present.
  hideLiveHelperChat()

  // Avoid duplicate observers/intervals.
  stopLiveHelperChatSuppression()

  const cleanup = () => hideLiveHelperChat()

  suppressionObserver = new MutationObserver(() => {
    cleanup()
  })
  suppressionObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // Some Safari async insertions can bypass one mutation tick.
  suppressionInterval = window.setInterval(cleanup, 1200)
}

export function stopLiveHelperChatSuppression() {
  if (suppressionObserver) {
    suppressionObserver.disconnect()
    suppressionObserver = null
  }
  if (suppressionInterval) {
    window.clearInterval(suppressionInterval)
    suppressionInterval = null
  }
}

export function openLiveHelperChat(config = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  const normalized = normalizeLhcConfig(config)

  // Best-effort: if widget exposes an API, use it; otherwise click the launcher; otherwise open the support site.
  try {
    if (window.LHC_API && typeof window.LHC_API.openWidget === 'function') {
      window.LHC_API.openWidget()
      return
    }
    if (window.LHC_API && typeof window.LHC_API.openChat === 'function') {
      window.LHC_API.openChat()
      return
    }
  } catch (e) {
    // fallthrough
  }

  try {
    const clickable =
      document.querySelector(`#${LHC_STATUS_CONTAINER_ID} a`) ||
      document.querySelector(`#${LHC_STATUS_CONTAINER_ID} button`)
    if (clickable) {
      clickable.click()
      return
    }
  } catch (e) {
    // fallthrough
  }

  // Last resort fallback: open hosted chat start page (may show offline/unavailable)
  window.open(normalized.fallbackStartUrl, '_blank')
}


